import { useState } from "react";
import "./index.css";

// ─── Types ────────────────────────────────────────────────────────────────────
interface TranscriptionResult {
  fileName: string;
  text: string;
  language?: string;
  durationInSeconds?: number;
  segments: { text: string; startSecond: number; endSecond: number }[];
  warnings?: string[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDuration(seconds: number) {
  if (!Number.isFinite(seconds)) return "--:--";
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(
    Math.floor(seconds % 60)
  ).padStart(2, "0")}`;
}

function formatBytes(bytes: number) {
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1_048_576).toFixed(1)} MB`;
}

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ─── Result Block ─────────────────────────────────────────────────────────────
function ResultBlock({
  result,
  copied,
  onCopy,
}: {
  result: TranscriptionResult;
  copied: boolean;
  onCopy: (text: string) => void;
}) {
  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-baseline justify-between pb-3 border-b border-[#e0dbd3] mb-5">
        <span className="text-[14px] font-semibold text-[#1a1a1a] tracking-tight">
          {result.fileName}
        </span>
        <div className="flex items-center gap-1.5 text-[11px] text-[#aaaaaa]">
          {result.language && <span>{result.language}</span>}
          {result.durationInSeconds != null && (
            <>
              <span>·</span>
              <span>{formatDuration(result.durationInSeconds)}</span>
            </>
          )}
          {result.segments.length > 0 && (
            <>
              <span>·</span>
              <span>
                {result.segments.length} segmento
                {result.segments.length !== 1 ? "s" : ""}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Transcript text */}
      <p className="text-[14px] leading-[1.75] text-[#333333] font-serif mb-7">
        {result.text}
      </p>

      {/* Segments timeline */}
      {result.segments.length > 0 && (
        <div className="mb-5">
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-[#bbbbbb] mb-3">
            Segmentos
          </div>
          <div className="border-l-[1.5px] border-[#e8e2da] pl-3.5 ml-1 flex flex-col">
            {result.segments.map((s, idx) => (
              <div
                key={`${s.startSecond}-${idx}`}
                className="flex items-baseline gap-3 py-1.5 border-b border-[#f0ece6] last:border-b-0"
              >
                <span className="font-mono text-[10.5px] text-[#bbbbbb] w-9 shrink-0 tabular-nums">
                  {formatDuration(s.startSecond)}
                </span>
                <span className="text-[12px] text-[#555555] leading-[1.5]">
                  {s.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2.5 pt-4 border-t border-[#f0ece6]">
        <button
          type="button"
          onClick={() => onCopy(result.text)}
          className="text-[11.5px] text-[#aaaaaa] border-b border-[#e0dbd3] leading-[1.2] pb-px hover:text-[#1a1a1a] hover:border-[#1a1a1a] transition-colors cursor-pointer"
        >
          {copied ? "Copiado ✓" : "Copiar tudo"}
        </button>
        <button
          type="button"
          onClick={() =>
            downloadText(
              `${result.fileName.replace(/\.[^.]+$/, "")}.txt`,
              result.text
            )
          }
          className="text-[11.5px] text-[#aaaaaa] border-b border-[#e0dbd3] leading-[1.2] pb-px hover:text-[#1a1a1a] hover:border-[#1a1a1a] transition-colors cursor-pointer"
        >
          Baixar .txt
        </button>
      </div>

      {result.warnings && result.warnings.length > 0 && (
        <div className="mt-4 flex flex-col gap-1">
          {result.warnings.map((w, i) => (
            <p key={i} className="text-[11px] text-[#aaaaaa] leading-[1.4]">
              ⚠ {w}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export function App() {
  const [fileInputKey, setFileInputKey] = useState(0);
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<TranscriptionResult[]>([]);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!files.length || loading) return;
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      for (const file of files) formData.append("audio", file);
      const res = await fetch("/api/transcribe", { method: "POST", body: formData });
      const body = (await res.json()) as {
        results?: TranscriptionResult[];
        error?: string;
      };
      if (!res.ok) throw new Error(body.error ?? "Falha na transcrição.");
      setResults((prev) => [...(body.results ?? []), ...prev]);
      setFiles([]);
      setFileInputKey((k) => k + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro desconhecido.");
    } finally {
      setLoading(false);
    }
  }

  function clearFiles() {
    setFiles([]);
    setFileInputKey((k) => k + 1);
    setError(null);
  }

  function removeFile(name: string) {
    setFiles((prev) => prev.filter((f) => f.name !== name));
  }

  async function copyToClipboard(idx: number, text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      window.setTimeout(() => setCopiedIdx(null), 1200);
    } catch {}
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith("audio/")
    );
    if (dropped.length) {
      setFiles((prev) => {
        const names = new Set(prev.map((f) => f.name));
        return [...prev, ...dropped.filter((f) => !names.has(f.name))];
      });
      setError(null);
    }
  }

  return (
    <div className="flex flex-col md:h-dvh md:overflow-hidden bg-[#f8f7f5]">
      {/* Topbar */}
      <header className="h-11 flex items-center px-5 gap-2 bg-[#f0ece6] border-b border-[#e0dbd3] shrink-0">
        <div className="w-[22px] h-[22px] rounded-[5px] bg-[#1a1a1a] flex items-center justify-center text-[11px]">
          🎙
        </div>
        <span className="text-[13px] font-semibold text-[#1a1a1a] tracking-tight">
          Transcriptor
        </span>
      </header>

      {/* Two-column layout */}
      <div className="flex flex-col md:flex-row flex-1 md:overflow-hidden">
        {/* Left panel */}
        <aside className="w-full md:w-[280px] md:shrink-0 bg-[#f0ece6] border-b md:border-b-0 md:border-r border-[#e0dbd3] flex flex-col p-5 gap-4 overflow-y-auto">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {/* Drop zone */}
            <div
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  document.getElementById("file-input")?.click();
                }
              }}
              className={`border-[1.5px] border-dashed rounded-[10px] p-6 flex flex-col items-center gap-2 cursor-pointer transition-colors ${
                dragging
                  ? "border-[#1a1a1a] bg-[#e8e2da]"
                  : "border-[#c8c0b5] hover:border-[#1a1a1a]"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDragEnd={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => document.getElementById("file-input")?.click()}
            >
              <div className="w-9 h-9 rounded-full bg-[#e8e2da] flex items-center justify-center text-base select-none">
                ↑
              </div>
              <p className="text-[11.5px] text-[#555555] text-center leading-[1.5] select-none">
                Arraste arquivos de áudio
                <br />
                ou clique para selecionar
              </p>
              <p className="text-[10px] text-[#aaaaaa] text-center select-none">
                MP3 · WAV · M4A · até 25 MB
              </p>
            </div>

            <input
              id="file-input"
              key={fileInputKey}
              type="file"
              name="audio"
              accept="audio/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const picked = Array.from(e.currentTarget.files ?? []);
                if (picked.length) {
                  setFiles((prev) => {
                    const names = new Set(prev.map((f) => f.name));
                    return [...prev, ...picked.filter((f) => !names.has(f.name))];
                  });
                  setError(null);
                }
              }}
            />

            {/* File list */}
            {files.length > 0 && (
              <div className="flex flex-col gap-1.5">
                {files.map((f) => (
                  <div
                    key={f.name}
                    className="flex items-center gap-2 px-2.5 py-2 bg-white rounded-[7px] border border-[#e8e2da]"
                  >
                    <span className="text-sm shrink-0">🎵</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-[#1a1a1a] truncate">
                        {f.name}
                      </p>
                      <p className="text-[9.5px] text-[#aaaaaa] mt-px">
                        {formatBytes(f.size)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(f.name)}
                      className="text-[#cccccc] hover:text-[#1a1a1a] text-base leading-none transition-colors cursor-pointer"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Inline error */}
            {error && (
              <p className="text-[11px] text-red-500 leading-[1.4]">{error}</p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={!files.length || loading}
              className="w-full py-2 bg-[#1a1a1a] text-[#f8f7f5] rounded-[7px] text-[12.5px] font-medium tracking-tight disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="inline-block w-3.5 h-3.5 border-[1.5px] border-[#555] border-t-[#f8f7f5] rounded-full animate-spin" />
                  Transcrevendo…
                </span>
              ) : (
                "Transcrever"
              )}
            </button>

            {files.length > 0 && !loading && (
              <button
                type="button"
                onClick={clearFiles}
                className="w-full py-1.5 bg-transparent text-[#aaaaaa] border border-[#e0dbd3] rounded-[7px] text-[11.5px] transition-colors hover:border-[#1a1a1a] hover:text-[#1a1a1a] cursor-pointer"
              >
                Limpar
              </button>
            )}
          </form>
        </aside>

        {/* Right panel */}
        <main className="flex-1 overflow-y-auto px-5 py-6 md:px-10 md:py-8">
          {results.length === 0 ? (
            <div className="flex flex-col items-center justify-center min-h-[280px] md:h-full gap-3 text-[#cccccc] select-none">
              <span className="text-4xl">🎙</span>
              <p className="text-[13px]">Envie um arquivo para começar</p>
            </div>
          ) : (
            <div className="flex flex-col gap-12 max-w-2xl">
              {results.map((r, idx) => (
                <ResultBlock
                  key={`${r.fileName}-${idx}`}
                  result={r}
                  copied={copiedIdx === idx}
                  onCopy={(text) => copyToClipboard(idx, text)}
                />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default App;
